import React, { useEffect, useState } from "react";
import {
  db,
  reverifyLocationCapabilities,
  declineEdLocationDataFlag,
  DATA_FLAG_BASIS_OPTIONS,
} from "@healthdesk/shared-services";
import "firebase/compat/storage";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { capitalize, getAddress, getStateCode } from "@healthdesk/shared-utils";
import { useSearch } from "@healthdesk/shared-hooks";
import firebase from "firebase/compat/app";
import * as geohash from "ngeohash";
import {
  DIAGNOSTIC_CAPABILITY_OPTIONS,
  getEffectiveDiagnosticCapabilities,
  isEmergencyDepartmentLocation,
} from "../../constants/diagnosticCapabilities";
import {
  getFacilityTypeLabel,
  isUrgentCare,
} from "../../constants/facilityTypes";
import {
  canEditFacilityType,
  capabilitiesEqual,
  normalizeCapsForWrite,
  urgentCareSubtypes,
} from "./addressSettingsFacilityType";

const DIAGNOSTIC_CAPABILITY_CATEGORIES = ["Imaging", "Lab", "Procedures"];

const AddressSettings = ({ location, visible, close, setSubmitted, userRole, dataFlagId }) => {
  const address = useSearch("", "address");
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState();
  const [firebaseError, setFirebaseError] = useState();
  const [dataFlag, setDataFlag] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineLoading, setDeclineLoading] = useState(false);

  useEffect(() => {
    if (!visible || !dataFlagId) {
      setDataFlag(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await db.collection("edLocationDataFlags").doc(dataFlagId).get();
        if (cancelled) return;
        if (snap.exists) {
          setDataFlag({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.warn("[AddressSettings] failed to load data flag:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, dataFlagId]);

  const updateLocation = async (updatedLocation) => {
    await db.collection("locations").doc(String(location.id)).update(updatedLocation);
  };

  const handleUrgentCareToggle = async (checked) => {
    setFirebaseError();
    setLoading(true);
    try {
      await updateLocation({
        facilitySubtypes: urgentCareSubtypes(checked),
      });
      setSubmitted(
        checked
          ? "Your facility is listed as accepting walk-in patients (Urgent Care)."
          : "Your facility is listed as appointments only."
      );
    } catch (e) {
      console.warn("[AddressSettings] facilitySubtypes update failed:", e);
      setFirebaseError(
        e?.message || "Unable to update facility type. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // If location data not loaded yet, return loading screen
  if (!location) {
    return (
      <Backdrop
        sx={{ color: "common.white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={visible}
        onClick={close}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const isEd = isEmergencyDepartmentLocation(location);
  const isUc = isUrgentCare(location);
  const canEditCaps = userRole === "chartmind-supervisor";
  const canEditFacilityTypeValue = canEditFacilityType(userRole);
  const effectiveCapsBaseline = getEffectiveDiagnosticCapabilities(location);

  const onSubmit = async ({ newlocation, group, diagnosticCapabilities }) => {
    const caps = Array.isArray(diagnosticCapabilities) ? diagnosticCapabilities : [];
    const capsChanged =
      canEditCaps && !capabilitiesEqual(caps, effectiveCapsBaseline);
    const groupChanged = (group ?? "") !== (location?.group ?? "");

    if (!newlocation && !groupChanged && !capsChanged) {
      setFirebaseError("Please update at least one field.");
      return;
    }

    setFirebaseError();
    setLoading(true);
    setSubmitted(false);

    const updatedLocation = {};

    try {
      // Diagnostic capabilities are attested through the supervisor-only callable so
      // reverification metadata is always written consistently.
      if (capsChanged) {
        await reverifyLocationCapabilities({
          locationId: String(location.id),
          diagnosticCapabilities: normalizeCapsForWrite(caps, location),
          ...(dataFlagId ? { dataFlagId } : {}),
        });
      }

      // Handle address update if provided
      if (newlocation) {
        const lookup = (name) =>
          newlocation.context.find((a) => a?.id.startsWith(name))?.text;
        const address = getAddress(newlocation);
        const city = lookup("place");
        const state = getStateCode(newlocation);
        const zip = lookup("postcode");
        const latitude = newlocation.center[1];
        const longitude = newlocation.center[0];
        const geopoint = new firebase.firestore.GeoPoint(latitude, longitude);
        const hash = geohash.encode(latitude, longitude);

        Object.assign(updatedLocation, {
          lat: latitude,
          lng: longitude,
          address,
          city,
          state,
          zip,
          coordinates: geopoint,
          g: {
            geohash: hash,
            geopoint,
          },
          addressEditedByHuman: true,
        });
      }

      // Handle group update if changed
      if (groupChanged) {
        updatedLocation.group = group ?? "";
      }

      if (Object.keys(updatedLocation).length > 0) {
        await updateLocation(updatedLocation);
      }

      setLoading(false);
      setSubmitted("Your facility capabilities and location settings were updated.");
      close();
    } catch (e) {
      console.warn("[AddressSettings] Update failed:", e);
      const message =
        e?.message ||
        "Unable to update facility settings. Please try again in a moment.";
      setFirebaseError(message);
      setLoading(false);
    }
  };

  let currentAddress =
    capitalize(`${location?.address}, ${location?.city}, `) +
    `${location?.state?.toUpperCase()} ${location?.zip}`;

  const basisLabel =
    DATA_FLAG_BASIS_OPTIONS.find((o) => o.value === dataFlag?.basis)?.label ||
    dataFlag?.basis ||
    "";

  const handleDeclineFlag = async () => {
    if (!dataFlagId || declineReason.trim().length < 5) {
      setFirebaseError("Please provide a brief reason (at least 5 characters).");
      return;
    }
    setDeclineLoading(true);
    setFirebaseError();
    try {
      await declineEdLocationDataFlag({
        flagId: dataFlagId,
        declineReason: declineReason.trim(),
      });
      setSubmitted("Your response was recorded. An administrator will complete the review.");
      close();
    } catch (e) {
      console.warn("[AddressSettings] decline flag failed:", e);
      setFirebaseError(e?.message || "Unable to submit decline.");
    } finally {
      setDeclineLoading(false);
    }
  };

  return (
    <Dialog open={visible} onClose={close} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Update Your Facility Information</DialogTitle>

        <DialogContent>
          {dataFlag ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              A report indicates this facility may have incorrect information (
              {basisLabel}). Please confirm or update your listing if needed. The reporter&apos;s
              identity is not shared with you.
              {dataFlag.specifics ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Report summary: {dataFlag.specifics}
                </Typography>
              ) : null}
            </Alert>
          ) : null}

          <DialogContentText variant="body2" sx={{ pb: 3 }}>
            Your current address is <strong>{currentAddress}</strong>.
          </DialogContentText>

          <Controller
            name="newlocation"
            control={control}
            rules={{
              validate: {
                required: (value) => {
                  if (value && !getAddress(value))
                    return "We weren't able to get an address from this location. Please choose a different address.";
                },
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                id="newlocation"
                options={address.suggestions}
                onChange={(event, item) => {
                  onChange(item);
                }}
                getOptionLabel={(option) => option.place_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={value || null}
                noOptionsText={"No results found"}
                loading={address.loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onChange={(newValue) => {
                      address.onChange(newValue);
                    }}
                    placeholder={currentAddress}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {address.loading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Facility Address"
                    variant="standard"
                    error={!!errors?.newlocation}
                    helperText={errors?.newlocation?.message}
                    sx={{ mb: 3 }}
                  />
                )}
              />
            )}
          />

          {/* Facility Group Name field */}
          <Controller
            name="group"
            control={control}
            defaultValue={location?.group || ""}
            rules={{
              maxLength: {
                value: 50,
                message: "Group name must be 50 characters or less.",
              },
              validate: {
                noNewlines: (value) =>
                  !value.includes("\n") ||
                  "Group name cannot contain line breaks.",
              },
            }}
            render={({ field }) => (
              <TextField
                id="group"
                label="Facility Group Name (Optional)"
                type="text"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.group}
                helperText={
                  errors?.group?.message ||
                  "Use the same name across multiple facility accounts to group them together on maps. Leave blank if you only have one location."
                }
                {...field}
              />
            )}
          />

          {/* Facility type — editable by facility/f2/admin only */}
          {!isEd ? (
            <Box sx={{ mb: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Facility type
              </Typography>

              {canEditFacilityTypeValue ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isUrgentCare(location)}
                      onChange={(e) => handleUrgentCareToggle(e.target.checked)}
                      size="small"
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Accepts unscheduled walk-in patients (Urgent Care)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enables your facility to appear in urgent care searches
                        even before diagnostic capability attestation.
                      </Typography>
                    </Box>
                  }
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getFacilityTypeLabel(location)}
                </Typography>
              )}
            </Box>
          ) : null}

          {/* Diagnostic capabilities — powers /scmap "facilities that have what you need" matching */}
          <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
            What can your facility do?
          </Typography>
          {!isUc ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Patients are matched to facilities based on the tests their condition may require.
              {isEd
                ? " For emergency departments, all capabilities are assumed until you uncheck services your ED does not offer."
                : " The more complete your profile, the more relevant patients you'll reach."}
            </Typography>
          ) : null}
          {isUc ? (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              <AlertTitle>Confirm your Urgent Care capabilities</AlertTitle>
              Your facility is listed as an Urgent Care. Patients can find you nearby, but your
              diagnostic capabilities will show as unverified until you complete this checklist.
              Check the services your facility offers.
            </Alert>
          ) : null}
          {!canEditCaps ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Only a chartmind-supervisor can update diagnostic capabilities. Contact your
              facility supervisor to change which services are listed.
            </Typography>
          ) : location?.capabilityVerification?.nextDeadlineAt ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Next capability reverification deadline:{" "}
              {new Date(
                typeof location.capabilityVerification.nextDeadlineAt?.toDate === "function"
                  ? location.capabilityVerification.nextDeadlineAt.toDate()
                  : location.capabilityVerification.nextDeadlineAt
              ).toLocaleDateString()}
            </Typography>
          ) : isEd ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uncheck any capability your ED does not provide, then save. Emergency departments
              appear on CareMap regardless of verification status.
            </Typography>
          ) : (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              Capabilities are currently unverified. A chartmind-supervisor must submit and
              reverify monthly to stay in default CareMap rankings.
            </Typography>
          )}

          <Controller
            name="diagnosticCapabilities"
            control={control}
            defaultValue={effectiveCapsBaseline}
            render={({ field: { value, onChange } }) => {
              const selected = Array.isArray(value) ? value : [];
              return (
                <>
                  {DIAGNOSTIC_CAPABILITY_CATEGORIES.map((category) => (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {category}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {DIAGNOSTIC_CAPABILITY_OPTIONS.filter(
                          (opt) => opt.category === category
                        ).map((opt) => (
                          <FormControlLabel
                            key={opt.key}
                            control={
                              <Checkbox
                                checked={selected.includes(opt.key)}
                                disabled={!canEditCaps}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...selected, opt.key]
                                    : selected.filter((k) => k !== opt.key);
                                  onChange(next);
                                }}
                              />
                            }
                            label={opt.label}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </>
              );
            }}
          />

          {firebaseError ? (
            <FormHelperText error sx={{ mt: 1 }}>
              {firebaseError}
            </FormHelperText>
          ) : null}

          {dataFlag && canEditCaps && dataFlag.status === "pending_facility" ? (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" gutterBottom>
                Information is correct — no change needed
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Reason (required to decline)"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleDeclineFlag}
                disabled={declineLoading}
              >
                {declineLoading ? "Submitting…" : "Decline — information is correct"}
              </Button>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Close</Button>
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            autoFocus
            variant="contained"
          >
            Update Facility
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddressSettings;
